import { Drawer, Form, Button, Space, message } from 'antd';
import { useCallback, useState, useMemo } from 'react';
import { FormInstance } from 'antd/lib/form';

interface UseFormDrawerOptions<T = any> {
  title: string;
  width?: number | string;
  onSubmit?: (values: T) => Promise<boolean>;
  onClose?: () => void;
  submitText?: string;
  cancelText?: string;
  placement?: 'right' | 'left';
  formProps?: {
    initialValues?: Partial<T>;
    [key: string]: any;
  };
}

interface DrawerFormProps {
  children: React.ReactNode;
  loading?: boolean;
  open?: boolean;
  className?: string;
}

export function useFormDrawer<T = any>({
  title,
  width = '80%',
  onSubmit,
  onClose,
  submitText = '确定',
  cancelText = '取消',
  placement = 'right',
  formProps = {},
}: UseFormDrawerOptions<T>) {
  const [form] = Form.useForm<T>();
  const [submitting, setSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const close = useCallback(() => {
    form.resetFields();
    onClose?.();
  }, [form, onClose]);

  const submit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (onSubmit) {
        const success = await onSubmit(values);
        if (success) {
          messageApi.success('保存成功');
          close(); // 只有在成功时才关闭抽屉
        }
      }
    } catch (error) {
      console.error('表单提交失败:', error);
    } finally {
      setSubmitting(false);
    }
  }, [form, onSubmit, close, messageApi]);

  const DrawerForm = useMemo(
    () =>
      ({
        children,
        loading = false,
        open = false,
        className,
      }: DrawerFormProps) => (
        <>
          {contextHolder}
          <Drawer
            title={title}
            width={width}
            onClose={close}
            open={open}
            placement={placement}
            styles={{
              body: { paddingBottom: 80 },
            }}
            className={className}
            destroyOnClose={false}
            maskClosable={false}
            extra={
              <Space>
                <Button onClick={close}>{cancelText}</Button>
                <Button
                  type="primary"
                  onClick={submit}
                  loading={submitting || loading}
                >
                  {submitText}
                </Button>
              </Space>
            }
          >
            <Form form={form} layout="vertical" {...formProps} preserve={false}>
              {children}
            </Form>
          </Drawer>
        </>
      ),
    [
      contextHolder,
      title,
      width,
      close,
      placement,
      submit,
      submitting,
      cancelText,
      submitText,
      form,
      formProps,
    ]
  );

  return {
    form,
    DrawerForm,
    close,
    resetFields: form.resetFields,
    setFieldsValue: form.setFieldsValue,
  };
}
